import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { usePlatform } from '../context/PlatformContext';
import { FaPlus, FaChartBar, FaMobileAlt, FaBell, FaUsers, FaMap, FaTrashAlt, FaUtensils, FaGift } from 'react-icons/fa';
import MissedBinModal from '../components/MissedBinModal';
import heroImage from '../assets/hero-image.jpg';
import './Home.css';

const Home = () => {
  const { currentUser, userProfile } = useAuth();
  const { isMobileApp } = usePlatform();
  const [showMissedBinModal, setShowMissedBinModal] = useState(false);
  const isCorporate = !isMobileApp && (userProfile?.role === 'subscriber' || 
                      userProfile?.subscription?.active || 
                      userProfile?.subscription?.type);
  const dashboardPath = isCorporate ? '/corporate-dashboard' : '/individual-dashboard';

  return (
    <div className="home">
      {/* Hero Section */}
      <section className="hero">
        <div className="hero-content">
          <div className="hero-text">
            <h1 className="hero-title">Track Waste in Real-Time</h1>
            <p className="hero-subtitle">
              Help keep your community clean by reporting waste incidents. 
              Join thousands of users making a difference.
            </p>
            <div className="hero-buttons">
              {!currentUser ? (
                <>
                  <Link to="/signup" className="btn btn-primary btn-large">
                    Get Started Free
                  </Link>
                  <Link to="/login" className="btn btn-outline btn-large">
                    Sign In
                  </Link>
                  <Link to="/map" className="btn btn-map btn-large">
                    <FaMap /> View Waste Map
                  </Link>
                </>
              ) : (
                <>
                  <Link to={dashboardPath} className="btn btn-primary btn-large">
                    <FaChartBar /> My Dashboard
                  </Link>
                  <Link to="/report" className="btn btn-secondary btn-large">
                    <FaPlus /> Report Waste
                  </Link>
                  <button 
                    className="btn btn-missed-bin btn-large"
                    onClick={() => setShowMissedBinModal(true)}
                  >
                    <FaTrashAlt /> Missed Bin Collection
                  </button>
                </>
              )}
            </div>
          </div>
          <div className="hero-image">
            <img src={heroImage} alt="Person collecting plastic waste" />
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="features">
        <div className="container">
          <h2 className="section-title">How It Works</h2>
          <div className="features-grid">
            <div className="feature-card">
              <div className="feature-icon">
                <FaMobileAlt />
              </div>
              <h3>Report Waste</h3>
              <p>Take photos, or type details of waste incidents from your mobile device</p>
            </div>

            <div className="feature-card">
              <div className="feature-icon">
                <FaMap />
              </div>
              <h3>Track on Map</h3>
              <p>View all reported waste on an interactive map with color-coded categories</p>
            </div>

            <div className="feature-card">
              <div className="feature-icon">
                <FaBell />
              </div>
              <h3>Get Alerts</h3>
              <p>Receive notifications about waste incidents in your selected locality/region</p>
            </div>

            <div className="feature-card">
              <div className="feature-icon">
                <FaUsers />
              </div>
              <h3>Community Driven</h3>
              <p>Work together with your community to identify and clean up waste</p>
            </div>

            <div className="feature-card">
              <div className="feature-icon">
                <FaChartBar />
              </div>
              <h3>Analytics Dashboard</h3>
              <p>{isMobileApp ? 'Track your personal waste reporting stats and impact' : 'Registered Users get access to comprehensive waste data and insights including waste types and environmental impact'}</p>
            </div>

            <div className="feature-card feature-card--food">
              <div className="feature-icon feature-icon--food">
                <FaUtensils />
              </div>
              <h3>Food Donations</h3>
              <p>Share surplus food with your community — set eat-by dates, allergen info, and collection deadlines</p>
            </div>

            <div className="feature-card feature-card--charity">
              <div className="feature-icon feature-icon--charity">
                <FaGift />
              </div>
              <h3>Charity Items</h3>
              <p>Donate clothes, furniture, electronics and more — rate condition and let charity shops claim them</p>
            </div>

            <div className="feature-card feature-card--missed-bin">
              <div className="feature-icon feature-icon--missed-bin">
                <FaTrashAlt />
              </div>
              <h3>Missed Bin Collection</h3>
              <p>Report missed bin collections instantly — your local authority is notified in real time</p>
            </div>

            {!isMobileApp && (
              <div className="feature-card">
                <div className="feature-icon">🏛️</div>
                <h3>For Organizations</h3>
                <p>Local councils, charities, waste recycling and removal companies can subscribe for detailed insights, and territory management</p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Categories Section */}
      <section className="categories-section">
        <div className="container">
          <h2 className="section-title">Waste and Resources Categories</h2>
          <div className="categories-grid">
            <div className="category-item" style={{ backgroundColor: '#bfdbfe' }}>
              <span className="category-icon">💻</span>
              <span>Electronic Waste</span>
            </div>
            <div className="category-item" style={{ backgroundColor: '#bbf7d0' }}>
              <span className="category-icon">🌿</span>
              <span>Biodegradable</span>
            </div>
            <div className="category-item" style={{ backgroundColor: '#fecaca' }}>
              <span className="category-icon">🏥</span>
              <span>Hospital & Medical</span>
            </div>
            <div className="category-item" style={{ backgroundColor: '#fde68a' }}>
              <span className="category-icon">🏗️</span>
              <span>Construction</span>
            </div>
            <div className="category-item" style={{ backgroundColor: '#fed7aa' }}>
              <span className="category-icon">🏠</span>
              <span>Domestic Waste</span>
            </div>
            <div className="category-item" style={{ backgroundColor: '#e9d5ff' }}>
              <span className="category-icon">🚗</span>
              <span>Automobile</span>
            </div>
            <div className="category-item" style={{ backgroundColor: '#cbd5e1' }}>
              <span className="category-icon">🏢</span>
              <span>Commercial</span>
            </div>
            <div className="category-item" style={{ backgroundColor: '#99f6e4' }}>
              <span className="category-icon">🌍</span>
              <span>Environmental</span>
            </div>
            <div className="category-item" style={{ backgroundColor: '#d1fae5' }}>
              <span className="category-icon">🍲</span>
              <span>Food Donation</span>
            </div>
            <div className="category-item" style={{ backgroundColor: '#ddd6fe' }}>
              <span className="category-icon">🎁</span>
              <span>Charity Items</span>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="container">
          <h2>Ready to Make a Difference?</h2>
          <p>Join our community and start reporting waste today</p>
          {!currentUser ? (
            <Link to="/signup" className="btn btn-primary btn-large">
              Sign Up Free
            </Link>
          ) : (
            <Link to={dashboardPath} className="btn btn-primary btn-large">
              Go to Dashboard
            </Link>
          )}
        </div>
      </section>

      {currentUser && (
        <MissedBinModal 
          isOpen={showMissedBinModal} 
          onClose={() => setShowMissedBinModal(false)} 
        />
      )}
    </div>
  );
};

export default Home;
